import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import SubscriptionGate from '../../components/subscription/SubscriptionGate';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { 
  FileText, 
  Copyright, 
  Award,
  Shield,
  DollarSign,
  Search,
  Plus,
  Building2,
  CheckCircle2,
  AlertTriangle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function IPRegistryPage() {
  const [activeTab, setActiveTab] = useState('register');
  const [ipType, setIpType] = useState('trademark');
  const [formData, setFormData] = useState({
    name: '',
    owner: '',
    description: ''
  });
  const [portfolio, setPortfolio] = useState(null);
  const [valuation, setValuation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  const tabs = [
    { id: 'register', label: 'Register IP', icon: Plus },
    { id: 'portfolio', label: 'My Portfolio', icon: Building2 },
    { id: 'search', label: 'Search Registry', icon: Search },
    { id: 'file-trademark', label: 'File Trademark', icon: Award }
  ];

  const ipTypes = [
    { value: 'trademark', label: 'Trademark', icon: Award, color: 'blue' },
    { value: 'patent', label: 'Patent', icon: FileText, color: 'purple' },
    { value: 'copyright', label: 'Copyright', icon: Copyright, color: 'green' }
  ];

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleRegisterIP = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('corpcraft_token');
      
      const response = await axios.post(
        `${API_URL}/api/ip/register`,
        {
          type: ipType,
          name: formData.name,
          owner: formData.owner,
          description: formData.description
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.conflict_detected) {
        toast.warning(`IP registered, but name conflicts with existing entry`);
      } else {
        toast.success(`${ipType.charAt(0).toUpperCase() + ipType.slice(1)} registered successfully!`);
      }

      // Reset form
      setFormData({ name: '', owner: '', description: '' });
      
      // Refresh portfolio if owner matches
      if (portfolio && portfolio.owner === formData.owner) {
        loadPortfolio(formData.owner);
      }

    } catch (error) {
      console.error('IP registration error:', error);
      toast.error(error.response?.data?.detail || 'Failed to register IP');
    } finally {
      setLoading(false);
    }
  };

  const loadPortfolio = async (owner) => {
    if (!owner) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('corpcraft_token');
      
      const [reportRes, valuationRes] = await Promise.all([
        axios.get(`${API_URL}/api/ip/report/${encodeURIComponent(owner)}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/ip/valuation/${encodeURIComponent(owner)}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      setPortfolio(reportRes.data);
      setValuation(valuationRes.data);
    } catch (error) {
      console.error('Portfolio load error:', error);
      toast.error('Failed to load portfolio');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('corpcraft_token');
      
      const response = await axios.get(
        `${API_URL}/api/ip/search?q=${encodeURIComponent(searchQuery)}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      setSearchResults(response.data.results);
      
      if (response.data.results.length === 0) {
        toast.info('No results found');
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleFileTrademark = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('corpcraft_token');
      
      const response = await axios.post(
        `${API_URL}/api/ip/file-trademark`,
        {
          name: formData.name,
          owner: formData.owner
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      toast.success(`Trademark filed! Application ID: ${response.data.application_id}`);
      setFormData({ name: '', owner: '', description: '' });

    } catch (error) {
      console.error('Trademark filing error:', error);
      toast.error(error.response?.data?.detail || 'Failed to file trademark');
    } finally {
      setLoading(false);
    }
  };

  const getIPIcon = (type) => {
    const iconMap = {
      trademark: Award,
      patent: FileText,
      copyright: Copyright
    };
    return iconMap[type] || Shield;
  };

  const getIPColor = (type) => {
    const colorMap = {
      trademark: 'text-blue-400',
      patent: 'text-purple-400',
      copyright: 'text-green-400'
    };
    return colorMap[type] || 'text-slate-400';
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Shield className="w-8 h-8 text-blue-400" />
            IP Registry & Management
          </h1>
          <p className="text-slate-400 mt-2">
            Manage intellectual property assets and trademark filings
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-slate-800">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors border-b-2 ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-400'
                  : 'border-transparent text-slate-400 hover:text-white'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Register IP Tab */}
        {activeTab === 'register' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-slate-900 border-slate-700">
              <CardHeader>
                <CardTitle>Register New IP Asset</CardTitle>
                <CardDescription>
                  Register patents, trademarks, or copyrights
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleRegisterIP} className="space-y-4">
                  {/* IP Type Selector */}
                  <div>
                    <Label>IP Type</Label>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      {ipTypes.map((type) => (
                        <button
                          key={type.value}
                          type="button"
                          onClick={() => setIpType(type.value)}
                          className={`p-3 rounded-lg border transition-colors ${
                            ipType === type.value
                              ? 'border-blue-500 bg-blue-500/10'
                              : 'border-slate-700 hover:border-slate-600'
                          }`}
                        >
                          <type.icon className={`w-5 h-5 mx-auto mb-1 ${
                            ipType === type.value ? `text-${type.color}-400` : 'text-slate-400'
                          }`} />
                          <div className="text-xs">{type.label}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="name">IP Name</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="e.g., CorpCraft Sentinel"
                      required
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>

                  <div>
                    <Label htmlFor="owner">Owner / Company</Label>
                    <Input
                      id="owner"
                      name="owner"
                      value={formData.owner}
                      onChange={handleInputChange}
                      placeholder="e.g., CorpCraft Inc"
                      required
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>

                  <div>
                    <Label htmlFor="description">Description</Label>
                    <textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Describe the IP asset..."
                      rows={4}
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-md text-white"
                    />
                  </div>

                  <Button type="submit" disabled={loading} className="w-full">
                    {loading ? 'Registering...' : 'Register IP Asset'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-slate-900 to-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-400" />
                  Important Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <h4 className="font-semibold text-blue-400 mb-2">Registration Process</h4>
                  <ul className="text-sm text-slate-300 space-y-1 list-disc list-inside">
                    <li>IP assets are registered in the CorpCraft registry</li>
                    <li>Conflict detection checks for existing names</li>
                    <li>Each asset receives a unique ID and timestamp</li>
                  </ul>
                </div>

                <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                  <h4 className="font-semibold text-purple-400 mb-2">Valuation</h4>
                  <ul className="text-sm text-slate-300 space-y-1 list-disc list-inside">
                    <li>Patents: 5x base value multiplier</li>
                    <li>Trademarks: 3x base value multiplier</li>
                    <li>Copyrights: 2x base value multiplier</li>
                    <li>Age and detail increase valuation</li>
                  </ul>
                </div>

                <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <h4 className="font-semibold text-amber-400 mb-2">Disclaimer</h4>
                  <p className="text-sm text-slate-300">
                    This is an internal registry for portfolio tracking. For official USPTO 
                    trademark/patent filing, use the "File Trademark" tab.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Portfolio Tab */}
        {activeTab === 'portfolio' && (
          <div className="space-y-4">
            <Card className="bg-slate-900 border-slate-700">
              <CardContent className="pt-6">
                <div className="flex gap-4">
                  <Input
                    placeholder="Enter company/owner name..."
                    value={formData.owner}
                    onChange={(e) => setFormData({ ...formData, owner: e.target.value })}
                    className="bg-slate-800 border-slate-700"
                  />
                  <Button onClick={() => loadPortfolio(formData.owner)} disabled={loading}>
                    {loading ? 'Loading...' : 'Load Portfolio'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {valuation && (
              <>
                {/* Valuation Summary */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card className="bg-gradient-to-br from-blue-900/20 to-blue-800/20 border-blue-700/50">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm text-slate-400">Total IP Value</div>
                          <div className="text-2xl font-bold text-white mt-1">
                            ${valuation.total_ip_value.toLocaleString()}
                          </div>
                        </div>
                        <DollarSign className="w-8 h-8 text-blue-400" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-slate-900 border-slate-700">
                    <CardContent className="pt-6">
                      <div className="text-sm text-slate-400">Patents</div>
                      <div className="text-2xl font-bold text-purple-400 mt-1">
                        {valuation.breakdown.patents}
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-slate-900 border-slate-700">
                    <CardContent className="pt-6">
                      <div className="text-sm text-slate-400">Trademarks</div>
                      <div className="text-2xl font-bold text-blue-400 mt-1">
                        {valuation.breakdown.trademarks}
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-slate-900 border-slate-700">
                    <CardContent className="pt-6">
                      <div className="text-sm text-slate-400">Copyrights</div>
                      <div className="text-2xl font-bold text-green-400 mt-1">
                        {valuation.breakdown.copyrights}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* IP Assets List */}
                <Card className="bg-slate-900 border-slate-700">
                  <CardHeader>
                    <CardTitle>IP Assets</CardTitle>
                    <CardDescription>
                      {valuation.total_assets} asset{valuation.total_assets !== 1 ? 's' : ''} registered
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {valuation.assets.map((asset) => {
                        const IPIcon = getIPIcon(asset.type);
                        return (
                          <div 
                            key={asset.id}
                            className="p-4 bg-slate-800/50 rounded-lg border border-slate-700"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-start gap-3">
                                <div className="p-2 bg-slate-700 rounded-lg">
                                  <IPIcon className={`w-5 h-5 ${getIPColor(asset.type)}`} />
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <h4 className="font-semibold text-white">{asset.name}</h4>
                                    <Badge variant="outline" className="text-xs">
                                      {asset.type}
                                    </Badge>
                                  </div>
                                  <p className="text-sm text-slate-400 mt-1">{asset.description}</p>
                                  <div className="text-xs text-slate-500 mt-2">
                                    Registered: {new Date(asset.timestamp).toLocaleDateString()}
                                  </div>
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-sm text-slate-400">Est. Value</div>
                                <div className="text-lg font-bold text-green-400">
                                  ${asset.estimated_value.toLocaleString()}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        )}

        {/* Search Tab */}
        {activeTab === 'search' && (
          <div className="space-y-4">
            <Card className="bg-slate-900 border-slate-700">
              <CardContent className="pt-6">
                <div className="flex gap-4">
                  <Input
                    placeholder="Search by IP name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    className="bg-slate-800 border-slate-700"
                  />
                  <Button onClick={handleSearch} disabled={loading}>
                    <Search className="w-4 h-4 mr-2" />
                    Search
                  </Button>
                </div>
              </CardContent>
            </Card>

            {searchResults.length > 0 && (
              <Card className="bg-slate-900 border-slate-700">
                <CardHeader>
                  <CardTitle>Search Results</CardTitle>
                  <CardDescription>{searchResults.length} result(s) found</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {searchResults.map((result) => {
                      const IPIcon = getIPIcon(result.type);
                      return (
                        <div 
                          key={result.id}
                          className="p-4 bg-slate-800/50 rounded-lg border border-slate-700"
                        >
                          <div className="flex items-start gap-3">
                            <IPIcon className={`w-5 h-5 mt-1 ${getIPColor(result.type)}`} />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h4 className="font-semibold text-white">{result.name}</h4>
                                <Badge variant="outline">{result.type}</Badge>
                              </div>
                              <p className="text-sm text-slate-400 mt-1">Owner: {result.owner}</p>
                              <p className="text-sm text-slate-500 mt-1">{result.description}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* File Trademark Tab */}
        {activeTab === 'file-trademark' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-slate-900 border-slate-700">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="w-5 h-5 text-blue-400" />
                  File USPTO Trademark
                </CardTitle>
                <CardDescription>
                  Submit trademark application (mock workflow)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleFileTrademark} className="space-y-4">
                  <div>
                    <Label htmlFor="tm-name">Trademark Name</Label>
                    <Input
                      id="tm-name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="e.g., CorpCraft"
                      required
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>

                  <div>
                    <Label htmlFor="tm-owner">Owner / Company</Label>
                    <Input
                      id="tm-owner"
                      name="owner"
                      value={formData.owner}
                      onChange={handleInputChange}
                      placeholder="e.g., CorpCraft Inc"
                      required
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>

                  <Button type="submit" disabled={loading} className="w-full">
                    {loading ? 'Filing...' : 'File Trademark Application'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-slate-900 to-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle>Trademark Filing Process</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-500/10 rounded-lg">
                    <CheckCircle2 className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">1. Submission</h4>
                    <p className="text-sm text-slate-400 mt-1">
                      Application submitted to USPTO with unique ID
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="p-2 bg-purple-500/10 rounded-lg">
                    <FileText className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">2. Examination (30-60 days)</h4>
                    <p className="text-sm text-slate-400 mt-1">
                      USPTO examiner reviews application for conflicts
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="p-2 bg-amber-500/10 rounded-lg">
                    <Shield className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">3. Publication (30 days)</h4>
                    <p className="text-sm text-slate-400 mt-1">
                      Published in Official Gazette for opposition period
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="p-2 bg-green-500/10 rounded-lg">
                    <Award className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">4. Registration (90-120 days)</h4>
                    <p className="text-sm text-slate-400 mt-1">
                      Trademark registered and certificate issued
                    </p>
                  </div>
                </div>

                <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg mt-4">
                  <p className="text-xs text-amber-200">
                    <strong>Note:</strong> This is a mock workflow for demonstration. 
                    For real trademark filing, visit USPTO.gov
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
